import { createAppAsyncThunk } from "@app/createAppAsyncThunk"
import {
  CourseClassDto,
  CourseInstanceClient,
  CourseInstanceDto,
} from "@clients/courseInstanceClient"
import {
  CreateCourseInstanceWithClassesRequest,
  CreateCourseInstanceWithClassesResponse,
} from "@masterschool/course-builder-api"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import PendingDataState from "@utils/pendingDataState"

export type CourseInstanceState = {
  instances: PendingDataState<CourseInstanceDto[]>
  courseClasses: PendingDataState<CourseClassDto[]>
}

const getInitialState = (): CourseInstanceState => ({
  instances: { status: "idle" },
  courseClasses: { status: "idle" },
})
export const courseInstancesSlice = createSlice({
  name: "courseInstances",
  initialState: getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchCourseInstances.fulfilled,
        (state, action: PayloadAction<CourseInstanceDto[]>) => {
          state.instances = {
            status: "success",
            data: action.payload,
          }
        },
      )
      .addCase(fetchCourseInstances.pending, (state) => {
        state.instances = { status: "pending" }
      })
      .addCase(fetchCourseInstances.rejected, (state) => {
        state.instances = { status: "error" }
      })
      .addCase(
        fetchCourseClasses.fulfilled,
        (state, action: PayloadAction<CourseClassDto[]>) => {
          state.courseClasses = {
            status: "success",
            data: action.payload,
          }
        },
      )
      .addCase(fetchCourseClasses.pending, (state) => {
        state.courseClasses = { status: "pending" }
      })
      .addCase(fetchCourseClasses.rejected, (state) => {
        state.courseClasses = { status: "error" }
      })
      .addCase(
        setCourseInstanceMajorVersion.fulfilled,
        (state, action: PayloadAction<CourseInstanceDto>) => {
          if (state.instances.status !== "success") {
            return
          }

          state.instances.data = state.instances.data.map((instance) => {
            if (instance.id === action.payload.id) {
              return action.payload
            }
            return instance
          })
        },
      )
  },
})

export const fetchCourseInstances = createAppAsyncThunk(
  "courseInstances/fetch",
  async () => CourseInstanceClient.listAllCourseInstances(),
)

export const fetchCourseClasses = createAppAsyncThunk(
  "courseInstances/fetchClasses",
  async () => CourseInstanceClient.listAllCourseClasses(),
)

export const setCourseInstanceMajorVersion = createAppAsyncThunk(
  "courseInstances/setMajorVersion",
  async (args: { courseInstanceId: string; majorVersion: number }) =>
    CourseInstanceClient.setCourseInstanceMajorVersion(
      args.courseInstanceId,
      args.majorVersion,
    ),
)

export const createCourseInstanceWithClasses = createAppAsyncThunk(
  "courseInstances/createWithClasses",
  async (request: CreateCourseInstanceWithClassesRequest, thunkAPI) =>
    CourseInstanceClient.instantiateCourseWithClasses(request).then(
      async (response: CreateCourseInstanceWithClassesResponse) => {
        if (response.isNewlyCreated) {
          await thunkAPI.dispatch(
            fetchCourseInstances(), // Refresh instances after creation
          )
          await thunkAPI.dispatch(
            fetchCourseClasses(), // Refresh classes after creation
          )
        } else {
          await thunkAPI.rejectWithValue({
            message: "Course instance was not newly created",
          })
        }
        return response
      },
    ),
)

export default courseInstancesSlice.reducer
